import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';

export type SimilarRequirementsTableRow = {
  /** Display identity can exist without an open capability. */
  rowKey?: string;
  /** Callers supply only an authorized open value; foreign summaries omit it. */
  openValue?: string;
  workflowName: string;
  ownerEmail?: string | null;
  ownerUnit?: string | null;
  updatedAt?: string | null;
  /** Legacy/preformatted fallback when an ISO timestamp is unavailable. */
  lastUpdatedDisplay?: string | null;
  /** A false value renders the row but deliberately removes its open action. */
  canOpen: boolean;
  /** Server-computed access mode; ownership text is never an authorization signal. */
  readOnly?: boolean;
};

export interface SimilarRequirementsResultsProps {
  loading: boolean;
  error?: string | null;
  rows: SimilarRequirementsTableRow[];
  currentUserEmail?: string;
  onOpen: (row: SimilarRequirementsTableRow) => void;
  onRetry?: () => void;
  /** A safe generic notice for a result no longer available for discovery. */
  unavailable?: boolean;
}

const NO_ACCESS_TOOLTIP =
  'You do not have access to this workflow. Please contact the owner.';

const ELLIPSIS_2_LINES = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
};

/** Parse an ISO timestamp safely; returns null on bad/empty input. */
function safeDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function relativeTime(iso?: string | null): string {
  const date = safeDate(iso);
  if (!date) return '—';
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return '—';
  }
}

function absoluteTime(iso?: string | null): string {
  const date = safeDate(iso);
  if (!date) return '';
  try {
    return format(date, 'PPpp');
  } catch {
    return '';
  }
}

/**
 * The shared body used by both the workflow modal and Home Chat cards.
 * Callers adapt their private identifier to `openValue`: workflow pages use a
 * flow ID while Home Chat supplies a short-lived opaque handle.
 */
const SimilarRequirementsResults: React.FC<SimilarRequirementsResultsProps> = ({
  loading,
  error,
  rows,
  currentUserEmail,
  onOpen,
  onRetry,
  unavailable = false,
}) => {
  const isEmpty = !loading && !error && rows.length === 0;
  const showTable = !loading && !error && rows.length > 0;

  return (
    <>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 5,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Searching existing workflows…
          </Typography>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}

      {isEmpty && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="subtitle1" gutterBottom>
            No similar requirements found.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nothing closely matches this requirement yet.
          </Typography>
        </Box>
      )}

      {unavailable && (
        <Alert severity="info" sx={{ mt: showTable ? 1 : 0 }}>
          A workflow is no longer available.
        </Alert>
      )}

      {showTable && (
        <Table
          data-testid="similar-requirements-table"
          size="small"
          aria-label="Similar requirements results"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '42%' }}>Workflow</TableCell>
              <TableCell sx={{ width: '26%' }}>Owner</TableCell>
              <TableCell sx={{ width: '16%' }}>Unit</TableCell>
              <TableCell sx={{ width: '16%' }}>Last updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isOwn =
                !!currentUserEmail &&
                !!row.ownerEmail &&
                row.ownerEmail.toLowerCase() === currentUserEmail.toLowerCase();
              const canOpen = row.canOpen && !!row.openValue;
              const rowSx = canOpen ? undefined : { color: 'text.secondary' };
              return (
                <TableRow key={row.rowKey ?? row.openValue} sx={rowSx} hover>
                  <TableCell sx={rowSx}>
                    <Box sx={{ minWidth: 0 }}>
                      {canOpen ? (
                        <Tooltip title={row.workflowName}>
                          <Link
                            component="button"
                            type="button"
                            variant="body2"
                            align="left"
                            underline="hover"
                            color="primary"
                            onClick={() => onOpen(row)}
                            sx={{
                              fontWeight: 700,
                              textAlign: 'left',
                              textTransform: 'none',
                              ...ELLIPSIS_2_LINES,
                            }}
                          >
                            {row.workflowName}
                          </Link>
                        </Tooltip>
                      ) : (
                        <Tooltip title={NO_ACCESS_TOOLTIP}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontWeight: 700,
                              cursor: 'default',
                              ...ELLIPSIS_2_LINES,
                            }}
                          >
                            {row.workflowName}
                          </Typography>
                        </Tooltip>
                      )}
                      {canOpen && row.readOnly && (
                        <Typography variant="caption" color="text.secondary" display="block">View only</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={rowSx}>
                    {isOwn ? (
                      <Tooltip title={row.ownerEmail || ''}>
                        <Typography variant="body2" component="span">
                          You
                        </Typography>
                      </Tooltip>
                    ) : row.ownerEmail ? (
                      <Tooltip title={row.ownerEmail}>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.ownerEmail}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" component="span" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={rowSx}>
                    {row.ownerUnit ? (
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.ownerUnit}
                      </Typography>
                    ) : (
                      <Typography variant="body2" component="span" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={rowSx}>
                    <Tooltip title={absoluteTime(row.updatedAt)}>
                      <Typography variant="body2" component="span">
                        {row.updatedAt
                          ? relativeTime(row.updatedAt)
                          : row.lastUpdatedDisplay || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default SimilarRequirementsResults;
